import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../routes/routeKeys";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import AddCommission from "../ContractToClosed/components/AddCommission";
import AddKeyContract from "../ContractToClosed/components/AddKeyContract";
import CompleteStatusModal from "../ContractToClosed/components/CompleteStatusModal";
import Loader from "../../components/Loader";

const StageDetailPageArchive = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [config] = useAuth();
  const [loading, setLoading] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [keyContacts, setKeyContacts] = useState({});
  const [commissionBreakdown, setCommissionBreakdown] = useState({});
  const [contractData, setContractData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMilestoneIndex, setSelectedMilestoneIndex] = useState(null);
  const [completeModal, setCompleteModal] = useState(false);
  const [comment, setComment] = useState("");
  const [showCommentBox, setShowCommentBox] = useState(false);

  const milestoneRows = [
    ["Purchase Agreement Draft Distributed", "purchase_agreement_draft_distributed"],
    ["Executed Purchase Agreement", "executed_purchase_agreeement"],
    ["Earnest Deposit Confirmed", "earnest_deposite_confirmed"],
    ["Due Diligence Delivery Confirmed", "due_diligence_delivery_receipt_confirmed"],
    ["Title", "title"],
    ["Coordinate Inspections", "cordinate_inspection"],
    ["Due Diligence Expiration Date", "due_diligence_expiration_date"],
    ["Estoppel & SNDA Ordered", "estoppel_snda_ordered"],
    ["Financing Approval", "financing_approval"],
    ["Estoppel & SNDA Received", "estoppel_snda_received"],
    ["Commission Invoice & Wiring Instructions", "comission_invoice_wiring_instruction"],
    ["Scheduled Closing", "scheduled_closing"]
  ];

  const getMilestoneLabel = (key) => {
    const labels = {
      executed_loi: "Executed LOI",
      purchase_agreement_draft_distributed: "PA Draft Distributed",
      executed_purchase_agreeement: "Executed PSA",
      transaction_timeline_distributed: "Timeline Distributed",
      earnest_deposite_confirmed: "Earnest Deposit",
      due_diligence_delivery_receipt_confirmed: "DD Delivery",
      title: "Title",
      address_title_issues: "Address Title Issues",
      cordinate_inspection: "Inspections",
      survey_received: "Survey",
      environmental_report_received: "Environmental",
      appraisal_received: "Appraisal",
      due_diligence_expiration_date: "DD Expiration",
      estoppel_snda_ordered: "Estoppel/SNDA Ordered",
      financing_approval: "Financing Approval",
      building_insurance_obtained: "Insurance",
      estoppel_snda_received: "Estoppel/SNDA Received",
      comission_invoice_wiring_instruction: "Commission Invoice",
      review_settlement_statement: "Settlement Statement",
      scheduled_closing: "Scheduled Closing",
      closed_date: "Closed",
    };
    return labels[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleStatusChange = (index, newStatus) => {
    if (newStatus === "Completed") {
      setSelectedMilestoneIndex(index);
      setCompleteModal(true);
      return;
    }

    const updated = [...milestones];

    if (!updated[index]) {
      updated[index] = { status: "Pending", completedAt: null };
    }

    updated[index].status = newStatus;
    updated[index].completedAt = null;
    setMilestones(updated);

    const backendKey = milestones[index]?.key;  // FIXED

    axios.post(
      `${BASE_URL}/contract-to-close-milestone/${id}`,
      {
        key_name: backendKey,
        status: newStatus,
      },
      config
    );
  };



  const saveCompletedDate = async (date) => {
    if (selectedMilestoneIndex === null) return;

    const backendKey = milestones[selectedMilestoneIndex]?.key;

    await axios.post(
      `${BASE_URL}/contract-to-close-milestone/${id}`,
      {
        key_name: backendKey,
        status: "Completed",
        completed_date: date,
      },
      config
    );

    // Close modal here
    setCompleteModal(false);

    // Refresh data AFTER save is complete
    fetchContractDetails();

    setSelectedMilestoneIndex(null);
  };



  const fetchContractDetails = () => {
    setLoading(true);

    axios
      .get(`${BASE_URL}/contract-to-close/${id}`, config)
      .then((res) => {
        const data = res?.data?.data;
        if (!data) return;

        setContractData(data);

        // Your milestone keys in the exact order you want to display
        const milestoneKeys = [
          // "executed_loi",
          "purchase_agreement_draft_distributed",
          "executed_purchase_agreeement",
          // "transaction_timeline_distributed",
          "earnest_deposite_confirmed",
          "due_diligence_delivery_receipt_confirmed",
          "title",
          // "address_title_issues",
          "cordinate_inspection",
          // "survey_received",
          // "environmental_report_received",
          // "appraisal_received",
          "due_diligence_expiration_date",
          "estoppel_snda_ordered",
          "financing_approval",
          // "building_insurance_obtained",
          "estoppel_snda_received",
          "comission_invoice_wiring_instruction",
          // "review_settlement_statement",
          "scheduled_closing",
          // "closed_date",
        ];

        // Dynamically build milestones from real API data
        const filledMilestones = milestoneKeys.map((key) => {
          const statusKey = `${key}_status`;
          const completedDateKey = `${key}_completed_date`;
          const rawStatus = data[statusKey];           // e.g., 2, null, 1
          const rawCompletedDate = data[completedDateKey]; // e.g., "2025-11-27" or null
          return {
            key,
            label: getMilestoneLabel(key), // your existing label function
            status: (rawStatus === 3 || rawStatus === "3") ? "Completed" : ((rawStatus === 2 || rawStatus === "2") ? 'In Progress' : "Pending"),
            completedAt: rawCompletedDate ? new Date(rawCompletedDate) : null,
            rawStatus,
            rawCompletedDate,
          };
        });

        setMilestones(filledMilestones);

        // Set other data
        setKeyContacts(data?.key_contact || {});
        setCommissionBreakdown(data?.gross_commission || {});
      })
      .catch((err) => {
        console.error("Failed to fetch contract details:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // BROKER SHARE ($ or %)
  const calculateBrokerShare = () => {
    const brokers = Array.isArray(contractData?.broker_breakdowns) ? contractData?.broker_breakdowns : [];
    return brokers.reduce((sum = 0, item = {}) => {
      const val = Number(item.total_value);
      if (item.type !== "percent") {
        return Number(sum + val);
      } else {
        return Number(sum + val);
      }
    }, 0);
  };

  // BDS SHARE ($ or %)
  const calculateBdsShare = () => {
    const bds = Array.isArray(contractData?.bds_breakdowns) ? contractData?.bds_breakdowns : [];
    return bds.reduce((sum = 0, item = {}) => {
      const val = Number(item?.total_value ?? 0);          // fallback
      if (item.type !== "percent") {
        return Number(sum + val);
      } else {
        return Number(sum + val);
      }
    }, 0);
  };

  // COMPANY GROSS
  const calculateCompanyGross = () => {
    const gci = (contractData?.gross_commission_income > 0) ? contractData?.gross_commission_income : contractData?.executed?.gross_commission?.gci_to_company.replace(/,/g, '');
    const brokerShare = calculateBrokerShare();
    const bdsShare = calculateBdsShare();

    return gci - (brokerShare + bdsShare);
  };
  const onSuccess = () => {
    setLoading(false);
  };

  useEffect(() => {
    fetchContractDetails();
  }, [id]);

  return (
    <BaseLayout>
      <div className="mb-[20px]">
        <p
          role="button"
          className="tgreen-H head-5 underline cursor-pointer"
          onClick={() => navigate(ROUTES.SETTINGS.CONTRACT_TO_CLOSED_ARCHIVE)}
        >
          Back to Contract To Close Archive
        </p>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-md relative min-h-[400px]">
        {loading && (
          <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
            <Loader />
          </div>
        )}
        {/* ---------- TOP HEADER ---------- */}
        <div className="mb-6">
          <h2 className="head-2 dark-H">
            {contractData?.link?.property_name || ""} -{" "}
            <span className="text-gray-600">
              {[
                contractData?.link?.street_address,
                contractData?.link?.city,
                contractData?.link?.state,
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-6 gap-4 mb-8">
          {[
            ["CONTRACT PRICE", `$${contractData?.executed?.contract_price_format || 0}`],
            ["CONTRACT CAP RATE ", `${contractData?.executed?.contract_cap_rate_format || 0}%`],
            ["SCHEDULED CLOSING DATE", contractData?.scheduled_closing_completed_date],
            ["COMMISSION %", contractData?.executed?.company_gcl || 0],
            ["GCI TO COMPANY", `$${contractData?.executed?.gross_commission?.gci_to_company || 0}`],
          ].map(([label, value], idx) => (
            <div
              key={idx}
              className="p-4 rounded-2xl shadow-sm text-center bg-gray-100"
            >
              <p className="uppercase body-S dark-M">{label}</p>
              <h3 className="head-2 dark-H mt-1">{value}</h3>
            </div>
          ))}

          <div
            className="p-4 rounded-2xl shadow-sm text-center cursor-pointer bg-gray-100 transition"
            onClick={() => setShowCommentBox(true)}
          >
            {!showCommentBox ? (
              <p className="body-S dark-M">Click here to add comment</p>
            ) : (
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Type your comment here..."
                className="mt-1 w-full p-2 border border-gray-400 rounded-xl text-gray-800 focus:ring-2 focus:ring-green-600 outline-none bg-white"
                rows="3"
              />
            )}
          </div>
        </div>

        {/* ---------- MAIN GRID ---------- */}
        <div className="grid grid-cols-[2fr_1fr_1fr] gap-6 items-stretch">
          {/* ======================= MILESTONES ======================= */}
          <div className="p-5 rounded-2xl shadow-md bg-gray-100 flex flex-col">
            <h3 className="uppercase head-2 green-H mb-3">Milestones</h3>

            <div className="flex-grow overflow-x-auto overflow-y-auto max-h-[400px]">
              <table className="w-full text-sm text-gray-800 border-collapse">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 px-2 w-[35%] font-semibold">
                      Milestone
                    </th>
                    <th className="text-left py-2 px-2 w-[20%] font-semibold">
                      Due Date
                    </th>
                    <th className="text-left py-2 px-2 w-[25%] font-semibold">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {milestoneRows.map(([label, key], idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-200 hover:bg-gray-200 transition"
                    >
                      <td className="py-2 body-N px-2 whitespace-pre-line">
                        {label}
                      </td>

                      <td className="py-2 body-N px-2">
                        {contractData?.[key] || ""}
                      </td>

                      <td className="py-2 px-2 body-N">
                        {milestones[idx]?.status === "Completed" ? (
                          <span
                            className="body-N  cursor-pointer"
                            onClick={() =>
                              handleStatusChange(idx, "In Progress")
                            }
                          >
                            {milestones[idx]?.rawCompletedDate}
                          </span>
                        ) : milestones[idx]?.status === "In Progress" ? (
                          <span
                            className="text-yellow-700 text-lg cursor-pointer"
                            onClick={() =>
                              handleStatusChange(idx, "Completed")
                            }
                          >
                            ⏳
                          </span>
                        ) : (
                          ""
                          // <select
                          //   className="border border-gray-400 rounded-md text-sm px-1 py-1 bg-white"
                          //   value={milestones[idx]?.status || "Pending"}
                          //   onChange={(e) =>
                          //     handleStatusChange(idx, e.target.value)
                          //   }
                          // >
                          //   <option>Pending</option>
                          //   <option>In Progress</option>
                          //   <option>Completed</option>
                          // </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-5 rounded-2xl shadow-md bg-gray-100 flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h3 className="uppercase head-2 text-green-800">Key Contacts</h3>

              {/* <button
                className=""
                onClick={() => setIsModalOpen(true)}
              >
                <img src={Edit} alt="icon" className="sidebar-icons mr-3" />
              </button> */}
            </div>
            <div className="text-sm text-gray-800 flex-grow overflow-auto space-y-4">
              <div>
                <p className="font-semibold text-gray-900">
                  {contractData?.escrowAgent?.title || "Escrow Agent"}:
                </p>
                <div className="ml-4">
                  <p>
                    {contractData?.key_contact?.agent_company_name?.name}
                  </p>
                  <p>
                    {contractData?.key_contact?.agent?.name}
                  </p>
                  <p>
                    {contractData?.key_contact?.agent?.phone}
                  </p>
                  <p>
                    {contractData?.key_contact?.agent?.email}
                  </p>
                </div>
              </div>

              <hr className="border-gray-300" />

              <div>
                <p className="font-semibold text-gray-900">Seller:</p>

                <div className="ml-4 space-y-3">
                  {/* Seller Broker */}
                  {contractData?.key_contact?.seller?.seller_broker?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_broker.map((broker, i) => (
                          <p key={i}>
                            {broker.first_name} {broker.last_name}
                          </p>
                        ))}
                      </strong>
                      <p>Broker</p>
                    </div>
                  )}

                  {/* Seller BDs */}
                  {contractData?.key_contact?.seller?.seller_bds?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_bds.map((bd, i) => (
                          <p key={i}>
                            {bd.first_name} {bd.last_name}
                          </p>
                        ))}
                      </strong>
                      <p>BDS</p>
                    </div>
                  )}

                  {/* Seller Attorney */}
                  {contractData?.key_contact?.seller?.seller_attorney?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_attorney.map((att, i) => (
                          <p key={i}>
                            {att.first_name} {att.last_name}
                          </p>
                        ))}
                      </strong>
                      <p>Attorney</p>
                    </div>
                  )}

                  {/* Seller Business Partner */}
                  {contractData?.key_contact?.seller?.seller_business_partner?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_business_partner.map((att, i) => (
                          <p key={i}>
                            {att.first_name} {att.last_name}
                          </p>
                        ))}
                      </strong>
                      <p>Business Partner</p>
                    </div>
                  )}

                  {/* Seller Other */}
                  {contractData?.key_contact?.seller?.seller_other?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.seller.seller_other.map((att, i) => (
                          <p key={i}>
                            {att.first_name} {att.last_name}
                          </p>
                        ))}
                      </strong>
                      <p>Other</p>
                    </div>
                  )}
                </div>
              </div>

              <hr className="border-gray-300" />

              <div>
                <p className="font-semibold text-gray-900">Buyer:</p>

                <div className="ml-4 space-y-3">
                  {/* Buyer Broker */}
                  {contractData?.key_contact?.buyer?.buyer_broker?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_broker.map((broker, i) => (
                          <p key={i}>
                            {broker.first_name} {broker.last_name}
                          </p>
                        ))}
                      </strong>
                      <p>Broker</p>
                    </div>
                  )}

                  {/* Buyer BDs */}
                  {contractData?.key_contact?.buyer?.buyer_bds?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_bds.map((bd, i) => (
                          <p key={i}>
                            {bd.first_name} {bd.last_name}
                          </p>
                        ))}
                      </strong>
                      <p>BDS</p>
                    </div>
                  )}

                  {/* Buyer Attorney */}
                  {contractData?.key_contact?.buyer?.buyer_attorney?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_attorney.map((att, i) => (
                          <p key={i}>
                            {att.first_name} {att.last_name}
                          </p>
                        ))}
                      </strong>
                      <p>Attorney</p>
                    </div>
                  )}

                  {/* Buyer Business Partner */}
                  {contractData?.key_contact?.buyer?.buyer_business_partner?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_business_partner.map((att, i) => (
                          <p key={i}>
                            {att.first_name} {att.last_name}
                          </p>
                        ))}
                      </strong>
                      <p>Business Partner</p>
                    </div>
                  )}

                  {/* Buyer Other */}
                  {contractData?.key_contact?.buyer?.buyer_other?.length > 0 && (
                    <div>
                      <strong>
                        {contractData.key_contact.buyer.buyer_other.map((att, i) => (
                          <p key={i}>
                            {att.first_name} {att.last_name}
                          </p>
                        ))}
                      </strong>
                      <p>Other</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          <div className="p-5 rounded-2xl shadow-md bg-gray-100 flex flex-col overflow-x-auto max-h-[480px]">
            <div className="flex justify-between items-start mb-3">
              <h3 className="uppercase head-2 text-green-800 mb-3">
                Commission Breakdown
              </h3>
              {/* <button
                className=""
                onClick={() => setModalOpen(true)}
              >
                <img src={Edit} alt="icon" className="sidebar-icons mr-3" />
              </button> */}
            </div>

            <div className="text-sm text-gray-800 flex-grow overflow-auto space-y-1">

              {/* Total Commission */}
              <div className="flex justify-between font-semibold ">
                <p>Total Commission:</p>
                <p>
                  $
                  {contractData?.total_commission_price
                    ? contractData.total_commission_price.toLocaleString()
                    : contractData?.executed?.gross_commission?.gci_to_company || 0}
                </p>
              </div>

              {/* Broker of Record Fee */}
              <div className="flex justify-between">
                <p className="font-semibold">Broker of Record Fee:</p>
                <p>
                  $
                  {contractData?.broker_record_fee
                    ? contractData.broker_record_fee.toLocaleString()
                    : "0"}
                </p>
              </div>

              {/* Referral Fee */}
              <div className="flex justify-between">
                <p className="font-semibold">Referral Fee:</p>
                <p>
                  $
                  {contractData?.referal_fee
                    ? contractData.referal_fee
                    : "0.00"}
                </p>
              </div>

              {/* Misc Fee */}
              <div className="flex justify-between">
                <p className="font-semibold">Misc. Fee:</p>
                <p>
                  $
                  {contractData?.misc_fee
                    ? contractData.misc_fee
                    : "0.00"}
                </p>
              </div>

              {/* Gross Commission Income */}
              <div className="flex justify-between font-semibold  mt-2">
                <p>Gross Commission Income:</p>
                <p>${contractData?.gross_commission_income_format ? contractData?.gross_commission_income_format
                  : contractData?.executed?.gross_commission?.gci_to_company || 0}</p>
              </div>

              <hr className="my-2 border-gray-300" />

              {/* Buyer Side Brokers */}
              <p className="font-semibold text-gray-900 mt-3">Pay to Brokers</p>

              {contractData?.broker_breakdowns?.map((item, idx) => (
                <div key={idx} className="flex justify-between">
                  <div>
                    <p className="font-semibold">{item.broker_name || item.name || "N/A"}</p>
                    <p className="text-xs text-gray-500">{item.percentage}% Contribution</p>
                  </div>
                  <p>${item.total_value_format}</p>
                </div>
              ))}

              {/* Pay to BDS */}
              <p className="font-semibold text-gray-900 mt-3">Pay to BDS</p>

              {contractData?.bds_breakdowns?.map((item, idx) => (
                <div key={idx} className="flex justify-between">
                  <div>
                    <p className="font-semibold">{item.bds_name || item.name || "N/A"}</p>
                    <p className="text-xs text-gray-500">{item.percentage}% Contribution</p>
                  </div>
                  <p>${item.total_value_format}</p>
                </div>
              ))}

              <hr className="my-2 border-gray-300" />

              {/* Company Gross Commission */}
              <div className="flex justify-between font-semibold ">
                <p>Company Gross Commission:</p>
                <p>${contractData?.company_gross_commission_format ?? calculateCompanyGross()}</p>
              </div>
            </div>
          </div>

        </div>

        <AddKeyContract
          showModal={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            fetchContractDetails();
          }}
          id={id}
        />

        <AddCommission
          showModal={modalOpen}
          onClose={() => {
            setModalOpen(false);
            fetchContractDetails();
          }}
          id={id}
        />

        <CompleteStatusModal
          showModal={completeModal}
          onClose={() => {
            setCompleteModal(false)
          }

          }
          onSave={saveCompletedDate}
        />


      </div>
    </BaseLayout>
  );
};

export default StageDetailPageArchive;
